import React, { useState, useEffect } from "react";
import { FaEdit, FaTimes, FaUser, FaEnvelope, FaUniversity, FaIdBadge, FaCheckCircle, FaInfoCircle, FaLock, FaKey } from "react-icons/fa";
import api from "../utils/api";

export default function EditUserModal({ user, isOpen, onClose, onUserUpdated }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    campus: "",
    studentId: "",
    isActive: true
  });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        campus: user.campus || "",
        studentId: user.studentId || "",
        isActive: user.isActive !== undefined ? user.isActive : true
      });
      setNewPassword("");
      setConfirmPassword("");
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const trimmedNew = newPassword.trim();
    if (trimmedNew || confirmPassword.trim()) {
      if (trimmedNew.length < 6) {
        setError("New password must be at least 6 characters");
        setLoading(false);
        return;
      }
      if (trimmedNew !== confirmPassword.trim()) {
        setError("New password and confirmation do not match");
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        campus: formData.campus,
        studentId: formData.studentId,
        isActive: formData.isActive
      };
      if (trimmedNew) {
        payload.newPassword = trimmedNew;
      }
      await api.put(`/users/${user._id}`, payload);
      onUserUpdated();
      onClose();
    } catch (error) {
      console.error("Error updating user:", error);
      setError(error.response?.data?.error || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="relative bg-gray-900 p-8 rounded-2xl shadow-2xl w-full max-w-lg mx-2 max-h-[90vh] overflow-y-auto animate-fadeIn">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white text-2xl font-bold focus:outline-none group"
          aria-label="Close"
        >
          <FaTimes className="group-hover:scale-110 transition-transform" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <FaEdit className="text-blue-500 text-2xl" />
          <h2 className="text-2xl font-bold text-white">Edit User</h2>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-600 text-white rounded-lg border border-red-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaUser className="text-blue-400" />
              Full Name
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Enter the user's full name
                </div>
              </div>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
              required
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaEnvelope className="text-blue-400" />
              Login email (username)
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 max-w-xs text-left">
                  Used to sign in. Changing this updates the user&apos;s username for login.
                </div>
              </div>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
              required
              autoComplete="off"
            />
          </div>

          <div className="p-4 rounded-lg border border-gray-600 bg-gray-800/50 space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-200">
              <FaKey className="text-amber-400" />
              Reset password
              <span className="text-xs font-normal text-gray-500">(optional — leave blank to keep current)</span>
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-400 mb-1">
                <FaLock className="text-gray-500" />
                New password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
                placeholder="Min. 6 characters"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-400 mb-1">
                <FaLock className="text-gray-500" />
                Confirm new password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
                placeholder="Re-enter new password"
                autoComplete="new-password"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <FaUniversity className="text-blue-400" />
              Campus
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Select the user's campus location
                </div>
              </div>
            </label>
            <select
              name="campus"
              value={formData.campus}
              onChange={handleChange}
              className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
              required
            >
              <option value="">Select Campus</option>
              <option value="Isulan">Isulan</option>
              <option value="ACCESS">ACCESS</option>
              <option value="Tacurong">Tacurong</option>
              <option value="Bagumbayan">Bagumbayan</option>
              <option value="Kalamansig">Kalamansig</option>
              <option value="Lutayan">Lutayan</option>
              <option value="Palimbang">Palimbang</option>
            </select>
          </div>

          {user.role === "student" && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <FaIdBadge className="text-blue-400" />
                Student ID
                <div className="relative group">
                  <FaInfoCircle className="text-gray-500 cursor-help" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    Student's unique identification number
                  </div>
                </div>
              </label>
              <input
                type="text"
                name="studentId"
                value={formData.studentId}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-gray-800 text-white border border-gray-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
              />
            </div>
          )}

          <div className="flex items-center gap-3 p-4 bg-gray-800 rounded-lg border border-gray-600">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="w-5 h-5 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
            />
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <FaCheckCircle className="text-green-400" />
              Active Account
              <div className="relative group">
                <FaInfoCircle className="text-gray-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  Enable or disable user account access
                </div>
              </div>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Updating...
                </>
              ) : (
                <>
                  <FaEdit />
                  Update User
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 flex items-center justify-center gap-2"
            >
              <FaTimes />
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 